import React, { useState, useEffect } from 'react';
import { Gift, Tangent as Exchange, TrendingDown, History } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import Header from '../components/Header';

interface RewardStats {
  rp_balance: number;
  quota_reduction: number;
}

interface RewardTransaction {
  id: string;
  amount: number;
  type: 'earn' | 'spend' | 'exchange';
  description: string;
  created_at: string;
}

const RewardCenter: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<RewardStats | null>(null);
  const [transactions, setTransactions] = useState<RewardTransaction[]>([]);
  const [showExchangeModal, setShowExchangeModal] = useState(false);
  const [showReduceModal, setShowReduceModal] = useState(false);
  const [exchangeAmount, setExchangeAmount] = useState('');
  const [reduceAmount, setReduceAmount] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchRewardData();
  }, []);

  const fetchRewardData = async () => {
    try {
      setLoading(true);
      const [statsResult, transactionsResult] = await Promise.all([
        supabase
          .from('user_rewards')
          .select('*')
          .eq('user_id', user?.id)
          .single(),
        supabase
          .from('reward_transactions')
          .select('*')
          .eq('user_id', user?.id)
          .order('created_at', { ascending: false })
          .limit(10)
      ]);

      if (statsResult.error) throw statsResult.error;
      if (transactionsResult.error) throw transactionsResult.error;

      setStats(statsResult.data || { rp_balance: 0, quota_reduction: 0 });
      setTransactions(transactionsResult.data || []);
    } catch (error) {
      console.error('Error fetching reward data:', error);
      toast.error('Failed to load reward data');
    } finally {
      setLoading(false);
    }
  };

  const handleExchangeRP = async () => {
    if (!exchangeAmount) {
      toast.error('Please enter an amount');
      return;
    }

    const amount = parseInt(exchangeAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (amount > (stats?.rp_balance || 0)) {
      toast.error('Insufficient RP balance');
      return;
    }

    setProcessing(true);
    try {
      const { error } = await supabase.rpc('exchange_rp_for_balance', {
        rp_amount: amount
      });

      if (error) throw error;

      toast.success(`Successfully exchanged ${amount} RP for ₽${amount * 50}`);
      setShowExchangeModal(false);
      setExchangeAmount('');
      await Promise.all([
        fetchRewardData(),
        refreshUser()
      ]);
    } catch (error) {
      console.error('Error exchanging RP:', error);
      toast.error('Failed to exchange RP');
    } finally {
      setProcessing(false);
    }
  };

  const handleReduceQuota = async () => {
    if (!reduceAmount) {
      toast.error('Please enter an amount');
      return;
    }

    const amount = parseInt(reduceAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    const rpCost = amount * 10;
    if (rpCost > (stats?.rp_balance || 0)) {
      toast.error('Insufficient RP balance');
      return;
    }

    setProcessing(true);
    try {
      const { error } = await supabase.rpc('reduce_weekly_quota', {
        reduction_amount: amount
      });

      if (error) throw error;

      toast.success(`Successfully reduced weekly quota by ${amount} accounts`);
      setShowReduceModal(false);
      setReduceAmount('');
      await fetchRewardData();
    } catch (error) {
      console.error('Error reducing quota:', error);
      toast.error('Failed to reduce quota');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
        <Header />
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
      <Header />
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Reward Center</h2>
            <p className="text-gray-600 dark:text-gray-400">
              Earn and spend Reward Points (RP) to get benefits
            </p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">RP Balance</h3>
                <Gift className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
              </div>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">
                {stats?.rp_balance || 0} RP
              </p>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                Current quota reduction: {stats?.quota_reduction || 0} accounts
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Exchange Rate</h3>
                <Exchange className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div className="space-y-2">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  • 1 RP = ₽50 when exchanging for balance
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  • 10 RP = 1 account reduction from weekly quota
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 mb-8">
            <button
              onClick={() => setShowExchangeModal(true)}
              className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
            >
              <Exchange className="h-5 w-5 mr-2" />
              Exchange RP for Balance
            </button>
            <button
              onClick={() => setShowReduceModal(true)}
              className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
            >
              <TrendingDown className="h-5 w-5 mr-2" />
              Reduce Weekly Quota
            </button>
          </div>

          {/* Transaction History */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center">
                <History className="h-5 w-5 text-indigo-600 dark:text-indigo-400 mr-2" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Recent Transactions
                </h3>
              </div>
            </div>
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {transactions.length === 0 ? (
                <div className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                  No transactions yet
                </div>
              ) : (
                transactions.map((transaction) => (
                  <div key={transaction.id} className="px-6 py-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {transaction.description}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {new Date(transaction.created_at).toLocaleString()}
                        </p>
                      </div>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        transaction.amount > 0
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      }`}>
                        {transaction.amount > 0 ? '+' : ''}{transaction.amount} RP
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Exchange Modal */}
          {showExchangeModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  Exchange RP for Balance
                </h3>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Amount of RP to Exchange
                  </label>
                  <input
                    type="number"
                    min="1"
                    max={stats?.rp_balance || 0}
                    value={exchangeAmount}
                    onChange={(e) => setExchangeAmount(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    placeholder="Enter RP amount"
                  />
                  {exchangeAmount && !isNaN(parseInt(exchangeAmount)) && (
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                      You will receive: ₽{parseInt(exchangeAmount) * 50}
                    </p>
                  )}
                </div>
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={() => setShowExchangeModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleExchangeRP}
                    disabled={processing}
                    className={`px-4 py-2 text-sm font-medium text-white rounded-md ${
                      processing
                        ? 'bg-indigo-400 cursor-not-allowed'
                        : 'bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600'
                    }`}
                  >
                    {processing ? 'Processing...' : 'Exchange'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Reduce Quota Modal */}
          {showReduceModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  Reduce Weekly Quota
                </h3>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Number of Accounts to Reduce
                  </label>
                  <input
                    type="number"
                    min="1"
                    max={Math.floor((stats?.rp_balance || 0) / 10)}
                    value={reduceAmount}
                    onChange={(e) => setReduceAmount(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    placeholder="Enter number of accounts"
                  />
                  {reduceAmount && !isNaN(parseInt(reduceAmount)) && (
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                      Cost: {parseInt(reduceAmount) * 10} RP
                    </p>
                  )}
                </div>
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={() => setShowReduceModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleReduceQuota}
                    disabled={processing}
                    className={`px-4 py-2 text-sm font-medium text-white rounded-md ${
                      processing
                        ? 'bg-indigo-400 cursor-not-allowed'
                        : 'bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600'
                    }`}
                  >
                    {processing ? 'Processing...' : 'Reduce Quota'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RewardCenter;